/**
 * Compact wire format for landmark frames crossing the worker boundary.
 *
 * A naive `postMessage` of the MediaPipe result allocates ~42 plain objects per frame on the
 * receiving side, which at 30 Hz is exactly the kind of steady garbage that produces the
 * periodic GC stalls a musical instrument cannot afford. Packing into one flat Float32Array
 * lets the main thread decode into a preallocated pool with zero allocation.
 *
 * Layout (Float32Array), per hand, `STRIDE` floats each:
 *   [0]      handedness code (0 = Unknown, 1 = Left, 2 = Right)
 *   [1]      handedness score
 *   [2..64]  21 landmarks as x, y, z triples (image-normalized)
 */

import type { HandLandmarkSample, Handedness, NormalizedLandmark } from './types';

export const LANDMARK_COUNT = 21;
export const HAND_STRIDE = 2 + LANDMARK_COUNT * 3;
export const MAX_HANDS = 2;
export const FRAME_BUFFER_LENGTH = HAND_STRIDE * MAX_HANDS;

export interface EncodedHandFrame {
  handCount: number;
  data: Float32Array;
}

const HANDEDNESS_CODES: Record<Handedness, number> = { Unknown: 0, Left: 1, Right: 2 };
const HANDEDNESS_BY_CODE: readonly Handedness[] = ['Unknown', 'Left', 'Right'];

export function handednessToCode(handedness: Handedness): number {
  return HANDEDNESS_CODES[handedness] ?? 0;
}

export function codeToHandedness(code: number): Handedness {
  return HANDEDNESS_BY_CODE[code] ?? 'Unknown';
}

/**
 * Write one hand into `data` at slot `index`.
 * Landmarks beyond `LANDMARK_COUNT` are ignored; missing ones are written as zero.
 */
export function encodeHand(
  data: Float32Array,
  index: number,
  landmarks: readonly { x: number; y: number; z: number }[],
  handedness: Handedness,
  score: number,
): void {
  const base = index * HAND_STRIDE;
  if (base + HAND_STRIDE > data.length) return;
  data[base] = handednessToCode(handedness);
  data[base + 1] = score;
  for (let i = 0; i < LANDMARK_COUNT; i += 1) {
    const landmark = landmarks[i];
    const offset = base + 2 + i * 3;
    data[offset] = landmark?.x ?? 0;
    data[offset + 1] = landmark?.y ?? 0;
    data[offset + 2] = landmark?.z ?? 0;
  }
}

/** Preallocated decode target so the main thread never allocates per frame. */
export function createSamplePool(size = MAX_HANDS): HandLandmarkSample[] {
  const pool: HandLandmarkSample[] = [];
  for (let i = 0; i < size; i += 1) {
    const landmarks: NormalizedLandmark[] = [];
    for (let j = 0; j < LANDMARK_COUNT; j += 1) landmarks.push({ x: 0, y: 0, z: 0 });
    pool.push({ landmarks, handedness: 'Unknown', score: 0 });
  }
  return pool;
}

/**
 * Decode `handCount` hands from `data` into `pool`, returning a reused array view.
 * The returned array shares objects with `pool`; consumers must not retain it across frames.
 */
export function decodeHands(
  data: Float32Array,
  handCount: number,
  pool: HandLandmarkSample[],
  out: HandLandmarkSample[],
): HandLandmarkSample[] {
  out.length = 0;
  const count = Math.min(handCount, pool.length);
  for (let i = 0; i < count; i += 1) {
    const sample = pool[i];
    if (!sample) continue;
    const base = i * HAND_STRIDE;
    sample.handedness = codeToHandedness(data[base] ?? 0);
    sample.score = data[base + 1] ?? 0;
    for (let j = 0; j < LANDMARK_COUNT; j += 1) {
      const landmark = sample.landmarks[j];
      if (!landmark) continue;
      const offset = base + 2 + j * 3;
      landmark.x = data[offset] ?? 0;
      landmark.y = data[offset + 1] ?? 0;
      landmark.z = data[offset + 2] ?? 0;
    }
    out.push(sample);
  }
  return out;
}
